import type CalendarsCacheStore from "../store/schema/CalendarsCacheStore";
import type { CalendarId } from "owa-graph-schema";
import { defaultAccountKey, getAccountKeyForMailboxInfo, getMailboxKeyForMailboxInfo, isDefaultAccountKey, type AccountKey, type MailboxKey, type MailboxInfo, } from "owa-client-types";
import { isSameMailboxInfo, tryGetGlobalSettingsAccountMailboxInfo, } from "owa-account-source-list-store";

type FolderIdMappingKey = AccountKey | MailboxKey;

function getFolderIdMappingKey(mailboxInfo: MailboxInfo): FolderIdMappingKey {
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    if (!isDefaultAccountKey(accountKey)) {
        return accountKey;
    }
    
    const globalSettingsMailboxInfo = tryGetGlobalSettingsAccountMailboxInfo(false /* logGreyError */);
    return globalSettingsMailboxInfo && isSameMailboxInfo(mailboxInfo, globalSettingsMailboxInfo)
        ? defaultAccountKey
        : getMailboxKeyForMailboxInfo(mailboxInfo);
}

export function getAccountScopedCalendarIdByFolderId(store: CalendarsCacheStore, folderId: string, mailboxInfo: MailboxInfo): CalendarId | undefined {
    const accountKey = getFolderIdMappingKey(mailboxInfo);
    const calendarId = store.folderIdToCalendarIdByAccount.get(accountKey)?.get(folderId);
    if (calendarId) {
        return calendarId;
    }
    
    // The mapping key can change for the same mailbox once its account becomes resolvable, so fall
    // back to an indexer-independent mailbox comparison rather than missing the mapping entirely.
    for (const accountMapping of store.folderIdToCalendarIdByAccount.values()) {
        const mappedCalendarId = accountMapping.get(folderId);
        if (mappedCalendarId && isSameMailboxInfo(mappedCalendarId.mailboxInfo, mailboxInfo)) {
            return mappedCalendarId;
        }
    }
    
    return undefined;
}

export function getCalendarIdForFolderId(store: CalendarsCacheStore, folderId: string, mailboxInfo?: MailboxInfo): CalendarId | undefined {
    return mailboxInfo
        ? getAccountScopedCalendarIdByFolderId(store, folderId, mailboxInfo)
        : store.folderIdToCalendarId.get(folderId);
}

export function setCalendarIdForFolderId(store: CalendarsCacheStore, folderId: string, calendarId: CalendarId): void {
    const accountKey = getFolderIdMappingKey(calendarId.mailboxInfo);
    removeCalendarIdFromAccountMappings(store, folderId, calendarId.id);
    let accountMapping = store.folderIdToCalendarIdByAccount.get(accountKey);
    if (!accountMapping) {
        accountMapping = new Map([[folderId, calendarId]]);
        store.folderIdToCalendarIdByAccount.set(accountKey, accountMapping);
    }
    else {
        accountMapping.set(folderId, calendarId);
    }
    
    updateLegacyFolderIdMapping(store, folderId);
}

export function removeCalendarIdForFolderId(store: CalendarsCacheStore, folderId: string, calendarId: CalendarId): void {
    removeCalendarIdFromAccountMappings(store, folderId, calendarId.id);
    updateLegacyFolderIdMapping(store, folderId);
}

// Calendar ids are unique across the cache, so clear this calendar's mapping under every key in case
// it was written before its account key became resolvable.
function removeCalendarIdFromAccountMappings(store: CalendarsCacheStore, folderId: string, calendarId: string): void {
    for (const [accountKey, accountMapping] of [...store.folderIdToCalendarIdByAccount]) {
        if (accountMapping.get(folderId)?.id === calendarId) {
            accountMapping.delete(folderId);
            if (accountMapping.size === 0) {
                store.folderIdToCalendarIdByAccount.delete(accountKey);
            }
        }
    }
}

function updateLegacyFolderIdMapping(store: CalendarsCacheStore, folderId: string): void {
    const currentLegacyCalendarId = store.folderIdToCalendarId.get(folderId);
    let lastCalendarId: CalendarId | undefined;
    let defaultAccountCalendarId: CalendarId | undefined;
    let stillMappedLegacyCalendarId: CalendarId | undefined;
    
    for (const [accountKey, accountMapping] of store.folderIdToCalendarIdByAccount) {
        const calendarId = accountMapping.get(folderId);
        if (calendarId) {
            lastCalendarId = calendarId;
            if (accountKey === defaultAccountKey) {
                defaultAccountCalendarId = calendarId;
            }
            if (calendarId.id === currentLegacyCalendarId?.id) {
                stillMappedLegacyCalendarId = calendarId;
            }
        }
    }
    
    // Unscoped consumers still depend on the legacy map, so on cross-account collisions prefer the
    // default account, then the still-mapped legacy entry, then the last mapped account.
    const legacyCalendarId = defaultAccountCalendarId ?? stillMappedLegacyCalendarId ?? lastCalendarId;
    if (legacyCalendarId) {
        store.folderIdToCalendarId.set(folderId, legacyCalendarId);
    }
    else {
        store.folderIdToCalendarId.delete(folderId);
    }
}
